import React, { useState } from 'react';
import {
  Box,
  StepGroup,
  StepItem,
  Button,
  RazorpayIcon,
  ArrowRightIcon,
  Modal,
  ModalHeader,
  ModalBody,
  CheckIcon,
  StepItemIndicator,
  Alert,
  List,
  ListItem,
  ListItemText,
  useToast,
} from '@razorpay/blade/components';
import { useBreakpoint, useTheme } from '@razorpay/blade/utils';
import { FormProvider, useFormWithProvider } from 'forms';
import { flexibleBenefitsInitialState } from '../constants';
import { BasicDetails } from './BasicDetails';
import { AmountDetails } from './AmountDetails';
import { TaxDetails } from './TaxDetails';
import { ReviewStep } from './ReviewStep';
import { CheckIconWrapper } from './styles';
import { convertFormDataToFlexiBenefitPayload } from '../utils';
import { FlexibleBenefitFormData } from './types';
import { routePaths } from 'components/Routes/data';
import { useHistory } from 'react-router-dom';
import { isEmptyOrWhitespace, isEmptyArray } from './utils';
import { useMutation } from 'react-query';
import api from 'api';
import { FullScreenLoader } from 'components/ui/FullScreenLoader';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';

const steps = [
  { title: 'Basic', description: '', stepNumber: 1 },
  { title: 'Amount', description: '', stepNumber: 2 },
  { title: 'Tax', description: '', stepNumber: 3 },
  { title: 'Review', description: '', stepNumber: 4 },
];

const FormContent = ({
  onDismiss,
  editData,
}: {
  onDismiss: () => void;
  editData?: FlexibleBenefitFormData;
}) => {
  const { theme } = useTheme();
  const { matchedDeviceType } = useBreakpoint(theme);
  const isMobile = matchedDeviceType === 'mobile';
  const { formData, setFieldError, clearFieldError } =
    useFormWithProvider<FlexibleBenefitFormData>();

  const isEditFlow = !!editData;
  const toast = useToast();
  const createFlexiBenefitComponent = useMutation({
    mutationFn: api.flexibleBenefitComponents.mutations.createFlexibleBenefit,
    onSuccess: () => {
      toast.show({
        content: 'FBP component created successfully 🎉',
        color: 'positive',
        duration: 3000,
      });
      onDismiss();
    },
    onError: () => {
      toast.show({
        content: 'Something went wrong while creating the FBP component, Please try again.',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const editFlexibleBenefitComponent = useMutation({
    mutationFn: api.flexibleBenefitComponents.mutations.updateFlexibleBenefit,
    onSuccess: () => {
      toast.show({
        content: 'FBP component updated successfully 🎉',
        color: 'positive',
        duration: 3000,
      });
      onDismiss();
    },
    onError: () => {
      toast.show({
        content: 'Something went wrong while updating the FBP component, Please try again.',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const [currentStep, setCurrentStep] = React.useState(1);
  const [completedSteps, setCompletedSteps] = React.useState<number[]>([]);

  const isStepComplete = (_step: number): boolean => {
    switch (_step) {
      case 1:
        return !isEmptyOrWhitespace(formData.name) && !isEmptyOrWhitespace(formData.displayName);

      case 2: {
        const benefitSettings = formData.benefit_amount_settings;

        if (benefitSettings?.type !== 'LIST_OF_VALUES') {
          return !!benefitSettings?.value;
        }
        return !isEmptyArray(benefitSettings.options);
      }

      case 3: {
        const taxBehaviour = formData.tax_behaviour;
        const isTaxExemptInAnyRegime =
          taxBehaviour?.tax_exempt_in_old_regime || taxBehaviour?.tax_exempt_in_new_regime;

        if (isTaxExemptInAnyRegime) {
          return !isEmptyOrWhitespace(taxBehaviour?.tax_exemption_section);
        }
        return true;
      }

      case 4:
        return true;

      default:
        return false;
    }
  };

  const validateStep = (_step: number): boolean => {
    switch (_step) {
      case 1:
        clearFieldError('name');
        clearFieldError('displayName');
        return true;

      case 2: {
        let isValid = true;
        const benefitSettings = formData.benefit_amount_settings;

        if (benefitSettings?.type !== 'LIST_OF_VALUES') {
          const value = benefitSettings?.value;
          const isValidNumber = (val: string) => /^\d+$/.test(val);

          if (value) {
            if (!isValidNumber(String(value))) {
              if (benefitSettings?.unit === 'ACTUAL_AMOUNT') {
                setFieldError('maxLimitAmount', 'Please enter a valid number');
                isValid = false;
              } else if (benefitSettings?.unit === 'PERCENTAGE_OF_BASIC') {
                setFieldError('maxLimitPercentage', 'Please enter a valid number');
                isValid = false;
              }
            } else if (Number(value) <= 0) {
              if (benefitSettings?.unit === 'ACTUAL_AMOUNT') {
                setFieldError('maxLimitAmount', 'Amount must be greater than 0');
                isValid = false;
              } else if (benefitSettings?.unit === 'PERCENTAGE_OF_BASIC') {
                setFieldError('maxLimitPercentage', 'Percentage must be greater than 0');
                isValid = false;
              }
            } else if (benefitSettings.unit === 'PERCENTAGE_OF_BASIC' && Number(value) >= 100) {
              setFieldError('maxLimitPercentage', 'Percentage must be less than 100');
              isValid = false;
            } else {
              clearFieldError('maxLimitAmount');
              clearFieldError('maxLimitPercentage');
            }
          }
        } else {
          // LIST_OF_VALUES: empty check handled by isStepComplete
          clearFieldError('multipleValuesList');
        }

        return isValid;
      }

      case 3:
        clearFieldError('tax_exemption_section');
        return true;

      default:
        return true;
    }
  };

  const handleNextStep = (): void => {
    if (currentStep < steps.length) {
      if (validateStep(currentStep)) {
        setCompletedSteps((prev) => [...prev, currentStep]);
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleSubmit = (): void => {
    const payload = convertFormDataToFlexiBenefitPayload(formData);
    if (isEditFlow && editData?.id) {
      editFlexibleBenefitComponent.mutate({
        ...payload.salary_component_requests[0],
        id: editData?.id || '',
      });
    } else {
      createFlexiBenefitComponent.mutate(payload);
    }
  };

  const alertDescription = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <List variant="ordered" size="small">
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  If display name is updated, past salary registers and payslips will reflect the
                  new Display Name.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                Changes in Gross Salary Inclusion setting will be applied retrospectively for flexi allowances already disbursed                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Benefit type can&apos;t be updated.
                </ListItemText>
              </ListItem>
            </List>
          </>
        );
      case 2:
        return (
          <>
            <List variant="ordered" size="small">
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  If the declaration mode or benefit amount is updated, the changes will apply only
                  to future payroll months.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Benefit amount if updated, current declaration of all employees will be reset to
                  default value.
                </ListItemText>
              </ListItem>
            </List>
          </>
        );
      case 3:
        return (
          <>
            <List variant="ordered" size="small">
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Taxability setting update will be applied retrospectively for all payroll months
                  of this financial year
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Exemption limit of the updated section should be applicable for the financial year{' '}
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Proration, arrears changes will only apply to future payroll months.
                </ListItemText>
              </ListItem>
              <ListItem>
                <ListItemText color="surface.text.gray.subtle">
                  Unclaimed amount can’t be updated.
                </ListItemText>
              </ListItem>
            </List>
          </>
        );
      default:
        return <></>;
    }
  };

  const handlePreviousStep = (): void => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleEditBasicDetails = (): void => {
    setCurrentStep(1);
  };

  const handleEditAmountDetails = (): void => {
    setCurrentStep(2);
  };

  const handleEditTaxDetails = (): void => {
    setCurrentStep(3);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BasicDetails isEditFlow={!!editData} />;

      case 2:
        return <AmountDetails />;

      case 3:
        return <TaxDetails />;

      case 4:
        return (
          <ReviewStep
            onEditBasicDetails={handleEditBasicDetails}
            onEditAmountDetails={handleEditAmountDetails}
            onEditTaxDetails={handleEditTaxDetails}
          />
        );

      default:
        return null;
    }
  };

  const renderStepGroup = () => (
    <StepGroup orientation="vertical" size="medium">
      {steps.map((step) => (
        <StepItem
          key={step.stepNumber}
          title={step.title}
          description={step.description}
          marker={
            isEditFlow ? (
              <CheckIconWrapper type={currentStep >= step.stepNumber ? 'positive' : 'neutral'}>
                <CheckIcon
                  color={
                    currentStep >= step.stepNumber
                      ? 'feedback.icon.positive.intense'
                      : 'feedback.icon.neutral.intense'
                  }
                />
              </CheckIconWrapper>
            ) : currentStep > step.stepNumber ? (
              <CheckIconWrapper type="positive">
                <CheckIcon color="feedback.icon.positive.intense" />
              </CheckIconWrapper>
            ) : (
              <StepItemIndicator color={currentStep === step.stepNumber ? 'primary' : 'neutral'} />
            )
          }
          isSelected={currentStep === step.stepNumber}
          isDisabled={step.stepNumber > currentStep}
          onClick={() => step.stepNumber <= currentStep && setCurrentStep(step.stepNumber)}
          stepProgress={
            completedSteps.includes(step.stepNumber)
              ? 'full'
              : currentStep === step.stepNumber
              ? 'start'
              : 'none'
          }
        />
      ))}
    </StepGroup>
  );

  const renderFooter = () => (
    <Box
      padding="spacing.4"
      borderTopWidth="thin"
      borderTopColor="surface.border.gray.muted"
      width={'calc(100% - 240px)'}
      position={'fixed'}
      bottom={'0px'}
      left={'232px'}
      backgroundColor={'surface.background.gray.moderate'}>
      <Box display="flex" gap="spacing.3" justifyContent="flex-end" width="100%">
        <Box display="flex" gap="spacing.3">
          {currentStep !== 1 && (
            <Button variant="tertiary" onClick={handlePreviousStep}>
              Previous
            </Button>
          )}
          <Button
            variant="primary"
            icon={ArrowRightIcon}
            iconPosition="right"
            onClick={currentStep === steps.length ? handleSubmit : handleNextStep}
            isFullWidth={isMobile}
            isDisabled={!isStepComplete(currentStep)}
            isLoading={
              createFlexiBenefitComponent.isLoading || editFlexibleBenefitComponent.isLoading
            }>
            {currentStep === steps.length
              ? `${isEditFlow ? 'Modify' : 'Create'} Component`
              : 'Next'}
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box display="flex" height="100%" justifyContent={'center'}>
      <Box
        width="224px"
        padding="spacing.6"
        backgroundColor="surface.background.gray.moderate"
        borderRightColor="surface.border.gray.muted"
        borderRightWidth="thin"
        position="fixed"
        left={'spacing.3'}
        height={'100%'}>
        {renderStepGroup()}
      </Box>

      <Box flex={1} display="flex" flexDirection="column">
        <Box
          justifyContent={'center'}
          width={'calc(100%-232px)'}
          marginLeft={'232px'}
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}>
          <Box flex={1} overflow="auto" width={'704px'}>
            <Box display={'flex'} flexDirection={'column'} gap="spacing.9" marginTop={'spacing.8'}>
              {isEditFlow && currentStep !== 4 && (
                <Alert
                  color="information"
                  title="Modify Details"
                  description={alertDescription()}
                  isFullWidth
                  isDismissible={false}
                />
              )}
              <Box marginBottom="200px">{renderStepContent()}</Box>
            </Box>
          </Box>

          {renderFooter()}
        </Box>
      </Box>
    </Box>
  );
};

export const CreateFlexiBenefit = ({
  editData,
  isLoading,
  isError,
  onRefresh,
}: {
  editData?: FlexibleBenefitFormData | null;
  isLoading?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const history = useHistory();

  const onDismiss = () => {
    setIsOpen(false);
    history.push(routePaths.flexibleBenefits.listView);
  };
  const isEditFlow = editData?.id;

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={onDismiss}
      size="full"
      zIndex={1000}
      accessibilityLabel="Create new flexi benefit component">
      <ModalHeader
        title={isEditFlow ? 'Edit Flexi Component' : 'New Flexi Component'}
        leading={<RazorpayIcon size="medium" color="interactive.icon.positive.normal" />}
      />
      <ModalBody padding="spacing.0">
        {isLoading ? (
          <Box height={'100vh'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <FullScreenLoader />
          </Box>
        ) : isError ? (
          <Box
            height={'100%'}
            backgroundColor={'surface.background.gray.intense'}
            alignItems={'center'}
            justifyContent={'center'}
            display={'flex'}>
            <EmptyListView
              title="This page could not be loaded due to unknown error. "
              description=""
              isError={true}>
              <Box display={'flex'} flexDirection={'row'} gap={'spacing.4'} marginX={'auto'}>
                <Button
                  size="medium"
                  onClick={() => {
                    onRefresh?.();
                  }}>
                  Retry
                </Button>
                <Button
                  size="medium"
                  onClick={() => {
                    history.push(routePaths.flexibleBenefits.listView);
                  }}>
                  Contact support
                </Button>
              </Box>
            </EmptyListView>
          </Box>
        ) : (
          <FormProvider initialState={editData || flexibleBenefitsInitialState}>
            <FormContent onDismiss={onDismiss} editData={editData || undefined} />
          </FormProvider>
        )}
      </ModalBody>
    </Modal>
  );
};
